import { useCallback, useEffect, useState } from 'react';

/**
 * PWA install state + trigger, shared by the auto-banner
 * ({@link InstallPwaPrompt}) and the footer link
 * ({@link InstallAppLink}). Single source of truth so both surfaces
 * stay in sync — when the visitor accepts the install in one place,
 * the other vanishes too.
 *
 * Returns:
 *   • `isStandalone`  — already installed (PWA window or iOS standalone).
 *   • `isIos`         — running iOS Safari; programmatic install is
 *                       impossible, the user must use Share → Add to
 *                       Home Screen. Callers render an instructions
 *                       dialog instead of a "Install" CTA.
 *   • `canInstall`    — true when Chromium fired `beforeinstallprompt`
 *                       and the install sheet can be triggered with
 *                       a single call. Falsy on iOS and on non-PWA-
 *                       eligible setups.
 *   • `install()`     — triggers the native install sheet on Android /
 *                       Chromium. No-op (returns false) on iOS or when
 *                       the beforeinstallprompt event hasn't fired yet.
 *                       Returns true if the user accepted the install.
 *   • `markInstalled` — call after a successful install to update
 *                       state immediately (the `appinstalled` event
 *                       can lag on some browsers).
 */

type BIPEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

function detectStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia('(display-mode: standalone)').matches) return true;
  // iOS Safari uses the non-standard `navigator.standalone` flag.
  return Boolean((window.navigator as { standalone?: boolean }).standalone);
}

function detectIosSafari(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  const isIphoneOrIpad =
    /iPhone|iPad|iPod/.test(ua) ||
    // iPadOS 13+ identifies as macOS but still has touch.
    (ua.includes('Mac') && navigator.maxTouchPoints > 1);
  const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(ua);
  return isIphoneOrIpad && isSafari;
}

export interface UsePwaInstall {
  isStandalone: boolean;
  isIos: boolean;
  canInstall: boolean;
  install: () => Promise<boolean>;
  markInstalled: () => void;
}

export function usePwaInstall(): UsePwaInstall {
  const [bipEvent, setBipEvent] = useState<BIPEvent | null>(null);
  const [isStandalone, setIsStandalone] = useState<boolean>(detectStandalone);
  const [isIos] = useState<boolean>(detectIosSafari);

  useEffect(() => {
    const onBip = (e: Event) => {
      // Prevent the default mini-infobar so we can show our own surface.
      e.preventDefault();
      setBipEvent(e as BIPEvent);
    };
    const onInstalled = () => {
      setIsStandalone(true);
      setBipEvent(null);
    };
    // Display-mode can switch at runtime when the user installs / opens
    // from the home screen — keep the flag in sync without a reload.
    const mq = window.matchMedia('(display-mode: standalone)');
    const onModeChange = () => setIsStandalone(detectStandalone());

    window.addEventListener('beforeinstallprompt', onBip);
    window.addEventListener('appinstalled', onInstalled);
    mq.addEventListener?.('change', onModeChange);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBip);
      window.removeEventListener('appinstalled', onInstalled);
      mq.removeEventListener?.('change', onModeChange);
    };
  }, []);

  const install = useCallback(async (): Promise<boolean> => {
    if (!bipEvent) return false;
    try {
      await bipEvent.prompt();
      const choice = await bipEvent.userChoice;
      // The event is single-use; null it out either way so we don't
      // re-prompt with a consumed instance.
      setBipEvent(null);
      if (choice.outcome === 'accepted') {
        setIsStandalone(true);
        return true;
      }
      return false;
    } catch {
      setBipEvent(null);
      return false;
    }
  }, [bipEvent]);

  const markInstalled = useCallback(() => {
    setIsStandalone(true);
    setBipEvent(null);
  }, []);

  return {
    isStandalone,
    isIos,
    canInstall: bipEvent !== null,
    install,
    markInstalled,
  };
}
